'use client'

import { Avatar, Button, Icon, IconComponent } from '@graftcode/design-system'

import type { Workspace } from '@common/modules/workspaces'

import { getInitials, getWorkspaceLogoUrl } from '../../../Sidebar/Sidebar.helpers'
import { useMobileMenuContext } from '../../context'

import styles from '../../MobileMenu.module.scss'

interface WorkspacesViewProps {
  /** List of workspaces */
  workspaces: Workspace[]
  /** Currently selected workspace ID */
  currentWorkspaceId?: string
  /** Handler for workspace selection */
  onWorkspaceSelect: (workspaceId: string) => void
}

export function WorkspacesView({ workspaces, currentWorkspaceId, onWorkspaceSelect }: WorkspacesViewProps) {
  const { navigateToMain, onClose, onOpenWorkspaceSettings, onInviteWorkspaceClick } = useMobileMenuContext()

  const handleSettingsClick = () => {
    onOpenWorkspaceSettings?.()
    onClose()
  }

  const handleInviteClick = () => {
    onInviteWorkspaceClick?.()
    onClose()
  }

  return (
    <div className={styles.menuView}>
      {/* Header with back button */}
      <div className={styles.header}>
        <button className={styles.backButton} onClick={navigateToMain}>
          <IconComponent icon={Icon.ChevronLeftRegular} size={24} />
          <span>Select Workspace</span>
        </button>
        <div className={styles.headerActions}>
          <Button iconOnly size="l" variant="ghost" icon={Icon.DismissRegular} onClick={onClose} />
        </div>
      </div>

      {/* Workspaces List */}
      <div className={styles.content}>
        <div className={styles.submenuSection}>
          {/* Workspace Actions */}
          <div className={styles.submenuGroup}>
            <button
              className={`${styles.submenuItem} ${!onOpenWorkspaceSettings ? styles.disabled : ''}`}
              onClick={handleSettingsClick}
              disabled={!onOpenWorkspaceSettings}
            >
              <IconComponent icon={Icon.SettingsRegular} size={20} />
              <span>Settings</span>
            </button>
            <button
              className={`${styles.submenuItem} ${!onInviteWorkspaceClick ? styles.disabled : ''}`}
              onClick={handleInviteClick}
              disabled={!onInviteWorkspaceClick}
            >
              <IconComponent icon={Icon.PeopleAddRegular} size={20} />
              <span>Invite people</span>
            </button>
          </div>

          {/* Separator */}
          <div className={styles.submenuSeparator} />

          {/* Workspaces List */}
          <div className={styles.submenuGroup}>
            <div className={styles.submenuGroupLabel}>Switch workspace</div>
            {workspaces.map((workspace) => {
              const isSelected = currentWorkspaceId === workspace.id
              const initials = getInitials(workspace.name)
              return (
                <button
                  key={workspace.id}
                  className={`${styles.submenuItem} ${isSelected ? styles.selected : ''}`}
                  onClick={() => onWorkspaceSelect(workspace.id)}
                >
                  <div>
                    <Avatar size={20} src={getWorkspaceLogoUrl(workspace)} fallback={initials} />
                  </div>
                  <span>{workspace.name}</span>
                  {isSelected && <IconComponent icon={Icon.CheckmarkRegular} size={20} className={styles.checkmark} />}
                </button>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
